import { useEffect, useMemo, useState } from "react";
import { animate } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useLang } from "@/lib/lang";
import { bookingHref } from "@/content";
import { Reveal, Eyebrow } from "./Reveal";

const RATES = { electricity: [0.05, 0.1], gas: [0.05, 0.1] };
const fmt = (n) => new Intl.NumberFormat("ro-RO").format(Math.round(n / 10) * 10);

const Counter = ({ value, testId }) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const c = animate(v, value, { duration: 0.6, ease: "easeOut", onUpdate: setV });
    return () => c.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span data-testid={testId}>{fmt(v)}</span>;
};

const SpendRow = ({ k, label, value, onChange }) => (
  <div className="border-b border-line py-6" data-testid={`calc-row-${k}`}>
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-ink">{label}</span>
      <span className="font-mono text-sm text-gold">{fmt(value)} RON</span>
    </div>
    <Slider className="ev-slider mt-5" min={5000} max={500000} step={5000} value={[value]} onValueChange={(v) => onChange(v[0])} data-testid={`calc-slider-${k}`} />
  </div>
);

export const Calculator = () => {
  const { t } = useLang();
  const c = t.calc;
  const [active, setActive] = useState({ electricity: true, gas: true });
  const [spend, setSpend] = useState({ electricity: 80000, gas: 40000 });
  const [lo, hi] = useMemo(() => Object.keys(RATES).filter((k) => active[k]).reduce(([a, b], k) => [a + spend[k] * RATES[k][0], b + spend[k] * RATES[k][1]], [0, 0]), [active, spend]);

  return (
    <section id="calculator" className="section" data-testid="calculator-section">
      <div className="container-x">
        <Reveal><Eyebrow>{c.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><h2 className="h2 mt-6 max-w-3xl">{c.title}</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-4 text-zinc-300">{c.sub}</p></Reveal>
        <Reveal delay={0.1} className="mt-12 grid overflow-hidden rounded-[28px] border border-line lg:grid-cols-12">
          <div className="p-6 sm:p-10 lg:col-span-7">
            <div className="flex flex-wrap gap-2">
              {Object.keys(RATES).map((k) => (
                <button key={k} onClick={() => setActive({ ...active, [k]: !active[k] })} data-testid={`calc-toggle-${k}`} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${active[k] ? "border-gold bg-gold/10 text-ink" : "border-line text-zinc-400 hover:text-ink"}`}>
                  <span className={`grid h-4 w-4 place-items-center rounded-full border ${active[k] ? "border-gold bg-gold text-bg" : "border-zinc-500"}`}>{active[k] && <Check size={10} strokeWidth={3} />}</span>
                  {c.cats[k]}
                </button>
              ))}
            </div>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{c.monthly}</p>
            {Object.keys(RATES).filter((k) => active[k]).map((k) => (
              <SpendRow key={k} k={k} label={c.cats[k]} value={spend[k]} onChange={(v) => setSpend({ ...spend, [k]: v })} />
            ))}
          </div>
          <div className="relative flex flex-col justify-between gap-10 bg-surface-2 p-6 sm:p-10 lg:col-span-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{c.perMonth}</p>
              <p className="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl"><Counter value={lo} testId="calc-month-low" /> – <Counter value={hi} testId="calc-month-high" /> <span className="text-lg text-zinc-400">RON</span></p>
              <div className="my-8 h-px bg-line" />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{c.perYear}</p>
              <p className="mt-3 font-display text-4xl font-semibold text-emerald-400 sm:text-6xl"><Counter value={hi * 12} testId="calc-year-high" /></p>
              <p className="mt-1 text-sm text-zinc-400">min. {fmt(lo * 12)} RON</p>
              <div className="mt-8 flex items-center justify-between rounded-2xl border border-line px-5 py-4">
                <span className="text-sm text-zinc-300">{c.fee}</span>
                <span className="font-display text-xl text-gold">0 RON</span>
              </div>
            </div>
            <div>
              <a href={bookingHref()} target="_blank" rel="noopener noreferrer" className="btn-gold w-full justify-center" data-testid="calc-book-btn">{c.cta} <ArrowUpRight size={18} /></a>
              <p className="mt-5 text-xs leading-relaxed text-zinc-400">{c.disclaimer}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
